import React, { useState, useEffect, useRef } from 'react';
import { Message } from './Message';
import { ImageMessage } from './ImageMessage';
import { IframeMessage } from './IframeMessage';
import { PhotoUploadMessage } from './PhotoUploadMessage';
import { OptionsMessage } from './OptionsMessage';
import { ActionBlock } from './ActionBlock';
import { ChatContainer } from './ChatContainer';
import { FeedbackUI } from './FeedbackUI';
import { ContactButtonsBar } from './ContactButtonsBar';
import { Avatar } from './Avatar';
import { Send, LogOut } from 'lucide-react';
import { cn, applyNoZoomViewport, restoreViewport } from '@/lib/utils';
import type { Message as MessageType } from '@/types';

interface ChatWidgetProps {
    messages: MessageType[];
    botId?: string;
    sessionId?: string;
    apiBaseUrl?: string;
    customStyles: {
        primaryColor: string;
        secondaryColor: string;
        botAvatar: string;
        userAvatar: string;
        botMessageBg?: string;
        userMessageBg?: string;
        botMessageText?: string;
        userMessageText?: string;
        borderRadius?: number;
        backgroundStyle?: 'transparent' | 'glass' | 'solid';
        shadowIntensity?: number;
        shadowColor?: string;
        backgroundColor?: string;
        animationDuration?: number;
        typingDelay?: number;
        messageAppearDelay?: number;

        // Font size for message text (px)
        fontSize?: number;
    };
    onClose?: () => void;
    onRestart?: () => void;
    /** Return `false` when the message was rejected (e.g. validation); input is not cleared. */
    onSendMessage?: (message: string) => void | false;
    /** Resolves to an error message to show, or null when the photo was accepted. */
    onPhotoSubmit?: (file: File) => Promise<string | null>;
    isTyping?: boolean;
    inputEnabled?: boolean;
    // Lead submission state
    isSubmittingLead?: boolean;
    // Manual lead capture CTA
    leadCTAVisible?: boolean;
    // Undo functionality
    undoConfirmation?: { targetIndex: number } | null;
    onUndoRequest?: (index: number) => void;
    onUndoConfirm?: () => void;
    onUndoCancel?: () => void;
    canUndoToMessage?: (index: number) => boolean;
    // Feedback
    showFeedback?: boolean;
    onFeedbackSubmit?: (rating: number, comment?: string) => void;
    onEndConversation?: () => void;
    chatCompleted?: boolean;
    // Contact buttons
    contactButtons?: Array<{
        id: string;
        icon: string;
        iconType: 'lucide' | 'custom';
        tooltip: string;
        url: string;
        iconColor?: string;
        bgColor?: string;
    }>;
    contactButtonsVisible?: boolean;
    aiEnabled?: boolean;
    validationError?: string | null;
    onClearValidationError?: () => void;
    textInputMode?: React.HTMLAttributes<HTMLTextAreaElement>['inputMode'];
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({
    messages,
    botId,
    sessionId,
    apiBaseUrl,
    customStyles,
    onClose,
    onRestart,
    onSendMessage,
    onPhotoSubmit,
    isTyping = false,
    inputEnabled = true, // Default to enabled for backward compatibility
    isSubmittingLead = false,
    leadCTAVisible = false,
    undoConfirmation,
    onUndoRequest,
    onUndoConfirm,
    onUndoCancel,
    canUndoToMessage,
    showFeedback,
    onFeedbackSubmit,
    onEndConversation,
    chatCompleted,
    contactButtons,
    contactButtonsVisible = false,
    aiEnabled = false,
    validationError = null,
    onClearValidationError,
    textInputMode = 'text',
}) => {
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Auto-focus input when it becomes enabled
    useEffect(() => {
        if (inputEnabled && inputRef.current) {
            inputRef.current.focus();
        }
    }, [inputEnabled]);

    // Provide defaults if customStyles is undefined
    const styles = customStyles || {
        primaryColor: '#667eea',
        secondaryColor: '#764ba2',
        botAvatar: 'https://widget.zentri.ai/icons/assistant.jpg',
        userAvatar: 'https://widget.zentri.ai/icons/user.jpg',
        borderRadius: 16,
        backgroundStyle: 'solid' as const,
        backgroundColor: '#ffffff',
    };

    let towardsWhiteSolidBG = false;
    if (styles.backgroundColor && styles.backgroundStyle === 'solid') {
        const r = parseInt(styles.backgroundColor.slice(1, 3), 16);
        const g = parseInt(styles.backgroundColor.slice(3, 5), 16);
        const b = parseInt(styles.backgroundColor.slice(5, 7), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        towardsWhiteSolidBG = brightness > 128;
    }

    const handleSend = () => {
        if (inputValue.trim() && onSendMessage) {
            const accepted = onSendMessage(inputValue);
            if (accepted === false) return;
            setInputValue('');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleOptionSelect = (value: string) => {
        if (onSendMessage) {
            onSendMessage(value);
        }
    };

    return (
        <div className="zentri-flex zentri-flex-col zentri-h-full zentri-w-full zentri-relative">
            {/* Chat Container - Takes full space */}
            <ChatContainer isTyping={isTyping} className="zentri-flex-1" onClose={onClose} onRestart={onRestart} customStyles={styles}>
                {messages.map((message, index) => {
                    if (message.type === 'regular' || message.type === 'welcome' || message.type === 'system') {
                        const showUndo = canUndoToMessage?.(index) && !undoConfirmation && onUndoRequest;
                        return (
                            <Message
                                key={index}
                                message={message}
                                botAvatar={styles.botAvatar}
                                userAvatar={styles.userAvatar}
                                customStyles={styles}
                                onUndo={showUndo ? () => onUndoRequest(index) : undefined}
                            />
                        );
                    } else if (message.type === 'image_message') {
                        return (
                            <ImageMessage
                                key={index}
                                message={message}
                                botAvatar={styles.botAvatar}
                                customStyles={styles}
                            />
                        );
                    } else if (message.type === 'options') {
                        // Check if user has already selected an option (next message is from user)
                        const isDisabled = index + 1 < messages.length && messages[index + 1]?.sender === 'user';

                        return (
                            <OptionsMessage
                                key={index}
                                message={message}
                                botAvatar={styles.botAvatar}
                                customStyles={styles}
                                onOptionSelect={isDisabled ? undefined : handleOptionSelect}
                                disabled={isDisabled}
                            />
                        );
                    } else if (message.type === 'action_block') {
                        return (
                            <ActionBlock
                                key={index}
                                message={message}
                                botAvatar={styles.botAvatar}
                                botId={botId}
                                sessionId={sessionId}
                                apiBaseUrl={apiBaseUrl}
                                customStyles={styles}
                            />
                        );
                    } else if (message.type === 'photo_upload') {
                        // Locked once the visitor has moved past it
                        const isDisabled = index + 1 < messages.length && messages[index + 1]?.sender === 'user';
                        return (
                            <PhotoUploadMessage
                                key={index}
                                message={message}
                                botAvatar={styles.botAvatar}
                                customStyles={styles}
                                onSubmit={isDisabled || chatCompleted ? undefined : onPhotoSubmit}
                                disabled={isDisabled || !!chatCompleted}
                            />
                        );
                    } else if (message.type === 'iframe_block') {
                        return (
                            <IframeMessage
                                key={index}
                                message={message}
                                botAvatar={styles.botAvatar}
                                customStyles={styles}
                            />
                        );
                    }
                    return null;
                })}

                {/* Typing Indicator */}
                {isTyping && (
                    <div className="zentri-flex zentri-items-start zentri-gap-3 zentri-mb-4 zentri-animate-fade-in">
                        <Avatar src={styles.botAvatar} alt="Bot" size="md" />
                        <div
                            className="zentri-px-4 zentri-py-3 zentri-max-w-xs zentri-transition-all zentri-duration-500 zentri-ease-out zentri-border zentri-border-neutral-100"
                            style={{
                                backgroundColor: styles.botMessageBg || styles.secondaryColor || '#ffffff',
                                borderRadius: `${styles.borderRadius || 16}px`,
                                borderTopLeftRadius: '0',
                                borderBottomRightRadius: `${styles.borderRadius || 16}px`,
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <div className="zentri-flex zentri-items-center zentri-gap-1.5">
                                <div className="typing-dot" style={{ backgroundColor: styles.primaryColor || '#000000', boxShadow: `0 2px 4px ${(styles.primaryColor || '#000000')}40` }} />
                                <div className="typing-dot" style={{ backgroundColor: styles.primaryColor || '#000000', boxShadow: `0 2px 4px ${(styles.primaryColor || '#000000')}40` }} />
                                <div className="typing-dot" style={{ backgroundColor: styles.primaryColor || '#000000', boxShadow: `0 2px 4px ${(styles.primaryColor || '#000000')}40` }} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Feedback form — shown as a message card after End Conversation */}
                {showFeedback && (
                    <div className="zentri-mb-4">
                        <FeedbackUI
                            onSubmit={onFeedbackSubmit || (() => {})}
                            primaryColor={styles.primaryColor}
                            secondaryColor={styles.secondaryColor}
                        />
                    </div>
                )}
            </ChatContainer>

            {/* Input & Footer Area */}
            <div className="zentri-px-4 zentri-pb-4 zentri-flex zentri-flex-col zentri-gap-3">

                {/* Contact Buttons — shown in AI mode (replacing CTA) and in regular mode past welcome */}
                {(leadCTAVisible || contactButtonsVisible) && contactButtons && contactButtons.length > 0 && !undoConfirmation && !isSubmittingLead && !showFeedback && (
                    <ContactButtonsBar
                        buttons={contactButtons}
                        primaryColor={styles.primaryColor}
                    />
                )}

                {/* Undo Confirmation UI - Replaces input when undo is pending */}
                {undoConfirmation && onUndoConfirm && onUndoCancel ? (
                    <div className="zentri-bg-white zentri-rounded-lg zentri-border zentri-border-neutral-200 zentri-shadow-subtle zentri-flex zentri-items-center zentri-justify-between zentri-p-3 zentri-gap-3 zentri-animate-fade-in">
                        <span className="zentri-text-sm zentri-text-neutral-700 zentri-font-medium">
                            Undo to this message?
                        </span>
                        <div className="zentri-flex zentri-items-center zentri-gap-2">
                            <button
                                onClick={onUndoConfirm}
                                className="zentri-px-3 zentri-py-1.5 zentri-rounded-md zentri-bg-green-100 zentri-text-green-700 hover:zentri-bg-green-200 zentri-transition-all zentri-duration-200 zentri-text-sm zentri-font-medium"
                                aria-label="Confirm undo"
                            >
                                Yes
                            </button>
                            <button
                                onClick={onUndoCancel}
                                className="zentri-px-3 zentri-py-1.5 zentri-rounded-md zentri-bg-red-100 zentri-text-red-700 hover:zentri-bg-red-200 zentri-transition-all zentri-duration-200 zentri-text-sm zentri-font-medium"
                                aria-label="Cancel undo"
                            >
                                No
                            </button>
                        </div>
                    </div>
                ) : isSubmittingLead ? (
                    /* Lead submission loading indicator (centered typing dots) */
                    <div className="zentri-flex zentri-justify-center zentri-items-center zentri-py-2">
                        <div className="zentri-flex zentri-items-center zentri-gap-1.5">
                            <div
                                className="typing-dot"
                                style={{ backgroundColor: styles.primaryColor || '#000000', boxShadow: `0 2px 4px ${(styles.primaryColor || '#000000')}40` }}
                            />
                            <div
                                className="typing-dot"
                                style={{ backgroundColor: styles.primaryColor || '#000000', boxShadow: `0 2px 4px ${(styles.primaryColor || '#000000')}40` }}
                            />
                            <div
                                className="typing-dot"
                                style={{ backgroundColor: styles.primaryColor || '#000000', boxShadow: `0 2px 4px ${(styles.primaryColor || '#000000')}40` }}
                            />
                        </div>
                    </div>
                ) : inputEnabled && !showFeedback ? (
                    /* Input Box - Premium Design (hidden when disabled) */
                    <div className="zentri-flex zentri-flex-col zentri-w-full zentri-gap-1">
                    <div className="zentri-bg-white zentri-rounded-lg zentri-border zentri-border-neutral-200 zentri-shadow-subtle zentri-flex zentri-items-center zentri-p-2 zentri-gap-2 focus-within:zentri-shadow-card focus-within:zentri-border-brand-300 zentri-transition-all zentri-duration-200">
                        <textarea
                            ref={inputRef}
                            value={inputValue}
                            onChange={(e) => {
                                setInputValue(e.target.value);
                                onClearValidationError?.();
                            }}
                            onKeyPress={handleKeyPress}
                            onFocus={applyNoZoomViewport}
                            onBlur={restoreViewport}
                            placeholder="Type a message..."
                            rows={1}
                            inputMode={textInputMode}
                            autoComplete="off"
                            className="zentri-flex-1 zentri-bg-transparent zentri-appearance-none zentri-border-none zentri-outline-none zentri-box-border zentri-ring-0 focus:zentri-ring-0 zentri-text-[13px] zentri-text-neutral-800 placeholder:zentri-text-neutral-400 zentri-pl-2 zentri-py-2 zentri-resize-none zentri-min-h-[32px] zentri-max-h-[100px] zentri-leading-snug zentri-font-sans"
                        />

                        <button
                            onClick={handleSend}
                            disabled={!inputValue.trim()}
                            className={cn(
                                'zentri-h-7 zentri-w-7 zentri-rounded-md zentri-flex zentri-shrink-0 zentri-items-center zentri-justify-center zentri-transition-all zentri-duration-300 zentri-ease-out',
                                inputValue.trim()
                                    ? 'zentri-bg-gradient-to-br zentri-from-brand-600 zentri-to-brand-700 zentri-text-white zentri-shadow-subtle hover:zentri-shadow-elegant hover:zentri-scale-110 active:zentri-scale-95'
                                    : 'zentri-bg-neutral-100 zentri-text-neutral-300 zentri-cursor-not-allowed'
                            )}
                            aria-label="Send message"
                        >
                            <Send size={14} strokeWidth={2.5} />
                        </button>
                    </div>
                    {validationError && (
                        <p className="zentri-text-red-600 zentri-text-xs zentri-px-0.5" role="alert">
                            {validationError}
                        </p>
                    )}
                    </div>
                ) : !showFeedback && chatCompleted && onEndConversation ? (
                    /* End Conversation button — shown when input is disabled (chat complete) */
                    <button
                        onClick={onEndConversation}
                        className="zentri-w-full zentri-rounded-lg zentri-border zentri-border-neutral-200 zentri-px-4 zentri-py-2.5 zentri-text-sm zentri-font-medium zentri-text-neutral-600 zentri-shadow-subtle zentri-transition-all zentri-duration-200 hover:zentri-bg-neutral-50 zentri-flex zentri-items-center zentri-justify-center zentri-gap-2"
                        style={{ background: '#fff' }}
                    >
                        <LogOut size={14} strokeWidth={2} />
                        End Conversation
                    </button>
                ) : null}

                {/* Powered by + Privacy - Footer: solid background only when chatbot backgroundStyle is transparent; links transparent on solid/glass */}
                <div
                    className="zentri-flex zentri-flex-col zentri-items-center zentri-gap-1 zentri-py-2 zentri-rounded-lg zentri-transition-opacity zentri-duration-500 hover:zentri-opacity-100 zentri-opacity-90"
                    style={
                        (styles.backgroundStyle === 'transparent' || styles.backgroundStyle === 'solid')
                            ? { backgroundColor: 'rgba(15, 0, 30, 0.9)', paddingLeft: 12, paddingRight: 12, paddingTop: 8, paddingBottom: 8, marginTop: 12 }
                            : undefined
                    }
                >
                    <div className="zentri-flex zentri-justify-center zentri-items-center zentri-gap-3">
                    <a
                        href="https://zentri.ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`zentri-flex zentri-items-center zentri-gap-1.5 zentri-text-xs zentri-font-medium zentri-no-underline zentri-whitespace-nowrap zentri-transition-colors ${towardsWhiteSolidBG ? 'zentri-text-neutral-800' : 'zentri-text-neutral-200'} hover:zentri-text-brand-300`}
                    >
                        <img
                            src="https://app.zentri.ai/logo.png"
                            alt="Zentri"
                            style={{ width: 20, height: 20, maxWidth: 20, maxHeight: 20, objectFit: 'contain', display: 'block' }}
                        />
                        Powered by Zentri
                    </a>
                    <span className={`zentri-select-none ${towardsWhiteSolidBG ? 'zentri-text-neutral-800' : 'zentri-text-neutral-200'}`}>•</span>
                    <a
                        href="https://www.zentri.ai/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`zentri-text-xs zentri-font-medium  hover:zentri-text-brand-300 zentri-no-underline zentri-whitespace-nowrap zentri-transition-colors ${towardsWhiteSolidBG ? 'zentri-text-neutral-800' : 'zentri-text-neutral-200'}`}
                    >
                        Privacy Policy
                    </a>
                    </div>
                    {aiEnabled && (
                        <p className={`zentri-text-center zentri-text-[10px] zentri-leading-tight zentri-m-0 zentri-opacity-50 ${towardsWhiteSolidBG ? 'zentri-text-neutral-700' : 'zentri-text-neutral-300'}`}>
                            AI can make mistakes. Verify important info.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};
